import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import api from "../../utils/api";
import Stepper from '../../assets/components/Stepper';
import { ShieldCheck, CheckCircle } from "lucide-react"; // Importamos CheckCircle

const stripePromise = loadStripe('pk_test_51QGQTkCtf3ESElJ3xz7bgjmmmFde6W8qUOw4F1UuMT1vgfK5QR9jzNgsSERRDEWHMVZ936yhDBWwsypUtAa6zh5K00TCbWa5bm');

const CheckoutPage = () => {
    const { planName: urlPlanName } = useParams();
    const navigate = useNavigate();

    const [clientSecret, setClientSecret] = useState(null);
    const [planDetails, setPlanDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpgrade, setIsUpgrade] = useState(false);

    useEffect(() => {
        const storedUserData = sessionStorage.getItem('pendingUserData');
        const storedPlanId = sessionStorage.getItem('selectedPlanId');
        const upgradePlanData = sessionStorage.getItem('upgradePlanData');

        // Verificar si es un upgrade
        if (upgradePlanData) {
            try {
                const upgradeData = JSON.parse(upgradePlanData);
                setIsUpgrade(upgradeData.isUpgrade);
            } catch (error) {
                console.error("Error parsing upgrade data:", error);
            }
        }

        // Si es un upgrade, no necesitamos pendingUserData
        if (!storedPlanId && !storedUserData) {
            console.warn("Missing plan data. Redirecting to plans.");
            navigate('/services-plans');
            return;
        }

        const createPaymentIntent = async () => {
            try {
                const planResponse = await api.get(`/plan/${storedPlanId}`);
                if (!planResponse.data.success) throw new Error("Could not fetch plan details.");

                const plan = planResponse.data.content;
                setPlanDetails(plan);

                const amountInCents = Math.round(plan.price * 100);
                const paymentIntentResponse = await api.post("/stripe/create-payment-intent", {
                    amount: amountInCents,
                    currency: "usd",
                    plan_id: plan._id
                });
                setClientSecret(paymentIntentResponse.data.clientSecret);
            } catch (err) {
                console.error("Error setting up payment:", err);
                navigate("/services-plans");
            } finally {
                setIsLoading(false);
            }
        };

        createPaymentIntent();
    }, [navigate]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center">
                    <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-gray-700 text-lg mt-4">Preparing secure payment...</p>
                </div>
            </div>
        );
    }

    const appearance = { theme: "stripe" };
    const options = { clientSecret, appearance };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Stepper en la parte superior */}
                <div className="w-full max-w-2xl mx-auto mb-12">
                    <Stepper currentStep={3} />
                </div>

                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 tracking-tight">
                        Secure Checkout
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Complete your payment details below.
                    </p>
                </div>

                {/* Contenedor principal de dos columnas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-16">


                    {/* --- Columna Derecha: Resumen del Pedido (Movida) --- */}
                    <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8 h-fit ">
                        <h2 className="text-2xl font-semibold text-gray-800 border-b pb-4 mb-6">
                            Order Summary
                        </h2>

                        {planDetails ? (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center text-gray-800">
                                    <span className="text-lg font-bold">{planDetails.name}</span>
                                    <span className="text-lg font-bold">${planDetails.price.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center text-gray-500 text-sm mb-4">
                                    <span>Billed Monthly</span>
                                    <span>USD</span>
                                </div>

                                {/* --- INICIO: Renderizado dinámico de las características del plan --- */}
                                <div className="border-t pt-6 space-y-3">
                                    <h3 className="text-md font-semibold text-gray-700">What's included:</h3>
                                    {planDetails.features && planDetails.features.map((featureGroup, index) => (
                                        <div key={index} className="pl-2">
                                            <p className="font-semibold text-gray-600">{featureGroup.title}</p>
                                            <ul className="mt-2 space-y-2">
                                                {featureGroup.features.map((feature, fIndex) => (
                                                    <li key={fIndex} className="flex items-start text-sm text-gray-500">
                                                        <CheckCircle size={16} className="mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                                {/* --- FIN: Renderizado dinámico --- */}

                                <div className="border-t pt-4 mt-4">
                                    <div className="flex justify-between items-center text-xl font-bold text-gray-900">
                                        <span>Total</span>
                                        <span>${planDetails.price.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="mt-6 text-sm text-gray-500 flex items-center">
                                    <ShieldCheck size={16} className="mr-2 text-green-500" />
                                    <span>Secure payment powered by Stripe.</span>
                                </div>
                            </div>
                        ) : (
                            <p>Loading plan details...</p>
                        )}
                    </div>
                    {/* --- Columna Izquierda: Detalles de Pago (Movida) --- */}
                    <div className="mb-12">
                        {clientSecret && (
                            <Elements options={options} stripe={stripePromise}>
                                <CheckoutForm clientSecret={clientSecret} isUpgrade={isUpgrade} />
                            </Elements>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
